import { Module } from "webpack";
import { CheckAttachmentWithFilesResponse, RelatedToTypes } from "../attachement/attachement.model";
import { AuthData, SearchForEmailResponse } from "./auth.model";
import { throwError as observableThrowError, Observable, Subject, BehaviorSubject, timer, Subscription } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '@environment';

// API URLs from environment configuration
export const serverUrl = environment.apiUrl;
export const APIOneDriveUrl = environment.oneDriveApiUrl;
export const requestHeaders = {'Content-Type': 'application/json'}
export function requestHeaderWithAccessToken() {
  return {'Content-Type': 'application/json', 'Authorization': `Bearer ${getStoredAuth()?.accessToken}`}
}
export const requestHeaderForOneDrive = {"enctype": "multipart/form-data"}
const refreshMinutesTimeBeforeExpiration:number = 2;
// Calculate time until token expires (refresh 5 minutes before expiration)
const now = new Date();
let expirationTime;
export function isTimeToLogout():boolean {
  if(getStoredAuth() && getStoredAuth().expiresAt){
    expirationTime = new Date(getStoredAuth().expiresAt);
    if(now.getTime() >= expirationTime.getTime()){
      return true;
    }
    return false;
  }
  return true;
}
export function getUserShortName(name):string {
  let letters: string;
  if (name && name !== "") {
    var subString: string[] = name.split(" ");
    subString = subString.filter((x) => x !== "");
    if (subString.length > 1) {
      letters = subString[0][0].trim().toLocaleUpperCase();
      letters += subString[1][0].trim().toLocaleUpperCase();
    } else {
      letters = subString[0][0].toLocaleUpperCase();
    }
  }
  return letters;
}
export function getStoredAuth(): AuthData | null {
  try {
    const stored = localStorage.getItem('pamsAuth');
    let StoredAuth: AuthData;
    if(stored){
      StoredAuth = JSON.parse(stored);
      if(Math.max((new Date(StoredAuth.expiresAt).getTime() - new Date().getTime()) - (refreshMinutesTimeBeforeExpiration * 60 * 1000), 0) == 0){
        logout();
        return null;
      }
      return StoredAuth ? StoredAuth : null;
    }
    return null;
  } catch {
    return null;
  }
}
export function showStatusMessage(message: string, type: 'success' | 'error' | 'info') {
  const statusContainer = document.getElementById('status-messages');
  if (!statusContainer) return;

  const messageElement = document.createElement('div');
  messageElement.className = `status-message status-${type}`;
  messageElement.textContent = message;
  
  statusContainer.appendChild(messageElement);
  statusContainer.scrollTop = statusContainer.scrollHeight;
  
  // Remove old messages (keep last 5)
  while (statusContainer.children.length > 5) {
    statusContainer.removeChild(statusContainer.firstChild!);
  }
  
  // Auto-remove after 10 seconds for non-error messages
  //if (type !== 'error') {
    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.parentNode.removeChild(messageElement);
      }
    }, 7000);
  //}
}
export function hideStatusMessage() {
  const statusContainer = document.getElementById('status-messages');
  if (!statusContainer) return;

  statusContainer.innerHTML = "";
}
export function logout(){
  localStorage.removeItem('pamsAuth');
  //sessionStorage.removeItem('pamsAuth');
}
// Method 5: Comprehensive detection function
async function detectOutlookMode(): Promise<{mode: 'compose' | 'read' | 'unknown'; confidence: 'high' | 'medium' | 'low'; details: string;}> {
  try {
    const item = Office.context.mailbox.item;
    
    if (!item) {
      return {
        mode: 'unknown',
        confidence: 'high',
        details: 'No Office.context.mailbox.item available'
      };
    }
    
    // High confidence checks
    if (item.to && typeof item.to.getAsync === 'function') {
      return {
        mode: 'compose',
        confidence: 'high',
        details: 'Recipients (to) field is editable'
      };
    }
    
    if (item.from && !item.to) {
      return {
        mode: 'read',
        confidence: 'high',
        details: 'Has sender info but no recipient editing capability'
      };
    }
    
    // Medium confidence checks
    if (item.subject && typeof item.subject.setAsync === 'function') {
      return {
        mode: 'compose',
        confidence: 'medium',
        details: 'Subject field is editable'
      };
    }
    
    if (item.dateTimeCreated) {
      return {
        mode: 'read',
        confidence: 'medium',
        details: 'Has dateTimeCreated property (read-only property)'
      };
    }
    
    // Low confidence - fallback
    return {
      mode: 'unknown',
      confidence: 'low',
      details: 'Unable to determine mode from available properties'
    };
    
  } catch (error) {
    return {
      mode: 'unknown',
      confidence: 'low',
      details: `Error during detection: ${error.message}`
    };
  }
}
// get device info
export function generateDeviceFingerprint(){
  function getBrowserInfo(userAgent: string): { name: string; version: string } {
    let name = 'Unknown';
    let version = 'Unknown';
    
    if (userAgent.includes('Chrome')) {
      name = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      name = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      name = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Edge')) {
      name = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    
    return { name, version };
  }
  function getOSInfo(userAgent: string): { name: string; version: string } {
    let name = 'Unknown';
    let version = 'Unknown';
    
    if (userAgent.includes('Windows')) {
      name = 'Windows';
      if (userAgent.includes('Windows NT 10')) version = '10';
      else if (userAgent.includes('Windows NT 6.1')) version = '7';
    } else if (userAgent.includes('Mac OS')) {
      name = 'macOS';
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      version = match ? match[1].replace('_', '.') : 'Unknown';
    } else if (userAgent.includes('Linux')) {
      name = 'Linux';
    } else if (userAgent.includes('Android')) {
      name = 'Android';
      const match = userAgent.match(/Android (\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('iOS')) {
      name = 'iOS';
      const match = userAgent.match(/OS (\d+_\d+)/);
      version = match ? match[1].replace('_', '.') : 'Unknown';
    }
    
    return { name, version };
  }
  function getDeviceType(userAgent: string): 'mobile' | 'tablet' | 'desktop' | 'outlook' {
    /*if (/Mobile|Android|iPhone/i.test(userAgent)) {
      return 'mobile';
    } else if (/iPad|Tablet/i.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';*/
    return 'outlook';
  }
  function getPluginsFingerprint(): string {
    return Array.from(navigator.plugins)
      .map(plugin => plugin.name)
      .sort()
      .join(',');
  }
  function getFontsFingerprint(): string {
    function isFontAvailable(font: string): boolean {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;
      const testString = "mmmmmmmmmmlli";

      context.font = "72px monospace";
      const baselineSize = context.measureText(testString).width;

      context.font = `72px ${font}, monospace`;
      const newSize = context.measureText(testString).width;

      return newSize !== baselineSize;
    }
    // Basic font detection (can be enhanced)
    const fonts = ['Arial', 'Times New Roman', 'Courier New', 'Helvetica'];
    return fonts.filter(font => isFontAvailable(font)).join(',');
  }
  function simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx!.textBaseline = "top";
  ctx!.font = "14px Arial";
  ctx!.fillText("Device fingerprint", 2, 2);

  const canvasFingerprint = canvas.toDataURL();

  // Get browser and OS info
  const UserAgent = navigator.userAgent;
  const browserInfo = getBrowserInfo(UserAgent);
  const osInfo = getOSInfo(UserAgent);
  const DeviceType = getDeviceType(UserAgent);

  // Create comprehensive fingerprint
  const fingerprintData = [
    UserAgent,
    navigator.language,
    screen.width + "x" + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.platform,
    navigator.cookieEnabled,
    canvasFingerprint,
    getPluginsFingerprint(),
    getFontsFingerprint(),
  ].join("|");

  // Simple hash function (in production, use a proper hash library)
  const DeviceFingerprint = simpleHash(fingerprintData);

  return {
    DeviceFingerprint,
    UserAgent,
    ScreenResolution: `${screen.width}x${screen.height}`,
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    Language: navigator.language,
    Platform: navigator.platform,
    CookiesEnabled: navigator.cookieEnabled,
    BrowserName: browserInfo.name,
    BrowserVersion: browserInfo.version,
    OperatingSystem: osInfo.name,
    OsVersion: osInfo.version,
    DeviceType,
    DeviceId: 0,
    DeviceTypeId: 0,
    MachineName: "Outlook plugin",
  };
}
export async function checkPermissionToCreate(module: "contact" | "account" | "uploadFiles" | "activity"):Promise<{Result:boolean}> {
  let moduleURL = "";
  if (module == "contact") {
    moduleURL = "contacts/new";
  }
   else if (module == "account") {
    moduleURL = "market/clients";
  }
   else if (module == "uploadFiles") {
    moduleURL = "library/attachments";
  }
   else if (module == "activity") {
    moduleURL = "activities/all";
  }
  try {
    const response = await fetch(serverUrl + `Security/RolePermissions/getpageid`, {
      method: "POST",
      headers: requestHeaderWithAccessToken(),
      body: JSON.stringify({
        ID: moduleURL,
        SecurityAccessTypeID: 2,
        UserID: getStoredAuth().user.CompanyBranchID,
      }),
    });
    //console.log("checkPermissionToCreate: ", moduleURL, await response);
    return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export async function checkIfEmailAddressExist(mail: string):Promise<SearchForEmailResponse[]> {
  try {
    const response = await fetch(serverUrl + `person/accounts/getaccountsandcontactsbyemail?email=${mail}`, {
      method: "GET",
      headers: requestHeaderWithAccessToken(),
    });
    //console.log("checkIfEmailAddressExist: ", await response);
    if(response.ok)
      return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export async function checkIfAttachmentExist(attachmentIds: string[]):Promise<CheckAttachmentWithFilesResponse[]> {
  try {
    const response = await fetch(serverUrl + `Common/Files/getrelatedbycloudId`, {
      method: "POST",
      headers: requestHeaderWithAccessToken(),
      body:JSON.stringify(attachmentIds)
    });

    //console.log("checkIfAttachmentExist: ", await response);
    if(response.ok)
      return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export function generateLinkToPams(moduleId?: number, objectId?: number):string {
  if(!moduleId && !objectId){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/`;
  }
  else if(moduleId == RelatedToTypes.Job){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/sales/edit/${objectId}?tab=files&pm=false`;
  }
  else if(moduleId == RelatedToTypes.PurchaseJob){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/purchasing/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Procurement){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/procurement/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Project){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/projects/edit/${objectId}?tab=files`;
  }
  else if(moduleId == RelatedToTypes.Invoice){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/finance/invoices/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Payment){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/finance/payments/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Client){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/market/accounts/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Contact){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/contacts/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Task){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/tasks/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.Activity){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/activities/edit/${objectId}`;
    //return `https://${getStoredAuth().user.Subdomain}.pams.net/activities/1/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.InspectionProduct){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/warehouse/inspection/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.InspectionDefect){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/warehouse/inspection/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.lettersOfGuaranties){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/finance/letters-of-guaranties/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.MRQ){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/procurement/material-requisition/edit/${objectId}`;
  }
  else if(moduleId == RelatedToTypes.AccountRegistration){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/registeration-data/accounts/view`;// ${objectId}
  }
  else if(moduleId == RelatedToTypes.ProjectRegistration){
    return `https://${getStoredAuth().user.Subdomain}.pams.net/registeration-data/projects/edit/${objectId}`;
  }
}
export function generateIdFromEmail(email: string): string {
  email = email.replace("@", "_");
  email = email.replace(".", "");
  if(email.includes(".")){
    generateIdFromEmail(email);
  }
  return email;
}